import { create } from 'zustand';
import i18n from 'i18n';
import { ApiUtils, GetUserProfileResponse, ProfileApi } from '@shared/api';
import type { BaseReturn } from '@shared/types';

export interface UserProfileSplitData {
  firstName: string;
  lastName: string;
  address1?: string;
  address2?: string;
  city?: string;
  postCode?: string;
  country?: string;
  email?: string;
  mobileNumber?: string;
  mobilePhoneCountryCodePart?: string;
  mobilePhoneNumberPart?: string;
}

interface UseGetUserProfileStore {
  loading: boolean;
  data: GetUserProfileResponse | null;
  primaryData: UserProfileSplitData | null;
  secondaryData: UserProfileSplitData | null;
  error?: string | null;
  get: (accountId: string) => Promise<BaseReturn<GetUserProfileResponse | null>>;
  clear: () => void;
}

export const useGetUserProfileStore = create<UseGetUserProfileStore>((set) => ({
  loading: false,
  data: null,
  primaryData: null,
  secondaryData: null,
  error: null,

  get: async (accountId: string) => {
    try {
      set({ loading: true, data: null, primaryData: null, secondaryData: null, error: null });
      const result = await ProfileApi.getUserProfile(accountId);
      if (result) {
        const primaryData = {
          firstName: result.firstName,
          lastName: result.lastName,
          address1: result.primaryAddress1,
          address2: result.primaryAddress2,
          city: result.primaryCity,
          postCode: result.primaryPostCode,
          country: result.primaryCountry,
          email: result.email,
          mobileNumber: result.mobileNumber,
          mobilePhoneCountryCodePart: result.mobilePhoneCountryCodePart,
          mobilePhoneNumberPart: result.mobilePhoneNumberPart,
        };
        const secondaryData = {
          firstName: result.firstName,
          lastName: result.lastName,
          address1: result.secondaryAddress1,
          address2: result.secondaryAddress2,
          city: result.secondaryCity,
          postCode: result.secondaryPostCode,
          country: result.secondaryCountry,
          email: result.email,
          mobileNumber: result.mobileNumber,
          mobilePhoneCountryCodePart: result.mobilePhoneCountryCodePart,
          mobilePhoneNumberPart: result.mobilePhoneNumberPart,
        };
        set({ loading: false, data: result, primaryData, secondaryData });
        return { loading: false, data: result };
      }
      set({ loading: false });
      return { loading: false, data: null };
    } catch (e) {
      const error = i18n.t(`errors.api.${ApiUtils.getError(e)}`, ApiUtils.getError(e));
      set({ loading: false, error });
      return { loading: false, data: null, error };
    }
  },

  clear: () => set({ loading: false, data: null, primaryData: null, secondaryData: null, error: null }),
}));
